import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { History, Search } from 'lucide-react';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import RecordHistoryDrawer from '../../components/Audit/RecordHistoryDrawer';
import { ACTION_CLASSES, ACTION_LABELS, formatActor, formatAuditDate } from '../../components/Audit/auditLabels';
import { getAppHomePath } from '../../constants/navigation';
import { auditService } from '../../services/api';

const ACTIONS = ['', 'created', 'updated', 'deleted', 'restored'];

const SystemHistory = () => {
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);

  const { data: catalogData } = useQuery(['audit-catalog'], async () => (await auditService.catalog()).data);
  const catalog = catalogData?.items || [];

  const filters = useMemo(
    () => ({
      entity_type: entityType || undefined,
      action: action || undefined,
      q: q.trim() || undefined,
      limit: 200,
    }),
    [entityType, action, q]
  );

  const { data, isLoading, error } = useQuery(
    ['audit-history', filters],
    async () => (await auditService.history(filters)).data
  );
  const items = data?.items || [];

  return (
    <FormPageShell maxWidth="max-w-7xl">
      <PageHeader
        title="Histórico do sistema"
        description="Quem criou, alterou, excluiu ou restaurou cada cadastro."
        breadcrumbs={[{ label: 'Início', to: getAppHomePath() }, { label: 'Histórico' }]}
      />

      <div className="card mt-4">
        <div className="card-content space-y-4">
          <div className="grid gap-3 md:grid-cols-3">
            <label className="block text-sm">
              <span className="mb-1 block text-gray-600">Cadastro</span>
              <select className="input" value={entityType} onChange={(e) => setEntityType(e.target.value)}>
                <option value="">Todos</option>
                {catalog.map((item) => (
                  <option key={item.entity_type} value={item.entity_type}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-gray-600">Ação</span>
              <select className="input" value={action} onChange={(e) => setAction(e.target.value)}>
                {ACTIONS.map((value) => (
                  <option key={value || 'all'} value={value}>
                    {value ? ACTION_LABELS[value] : 'Todas'}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-gray-600">Buscar</span>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  className="input pl-9"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Nome, usuário, identificador…"
                />
              </div>
            </label>
          </div>

          {isLoading ? (
            <p className="text-sm text-gray-500">Carregando histórico…</p>
          ) : error ? (
            <p className="text-sm text-rose-600">Não foi possível carregar o histórico.</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-gray-500">Nenhuma ação encontrada com esses filtros.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Quando</th>
                    <th>Ação</th>
                    <th>Cadastro</th>
                    <th>Registro</th>
                    <th>Quem</th>
                    <th>O que aconteceu</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={item.id}
                      className="cursor-pointer hover:bg-primary-50/40"
                      onClick={() => setSelected(item)}
                    >
                      <td className="whitespace-nowrap text-sm text-gray-600">{formatAuditDate(item.occurred_at)}</td>
                      <td>
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${ACTION_CLASSES[item.action] || 'bg-gray-50 text-gray-700 ring-gray-200'}`}>
                          {ACTION_LABELS[item.action] || item.action}
                        </span>
                      </td>
                      <td className="text-sm text-gray-800">{item.entity_type_label || item.entity_type}</td>
                      <td className="text-sm text-gray-800">{item.entity_label || item.entity_id}</td>
                      <td className="text-sm text-gray-700">
                        {formatActor(item)}
                        {item.actor_role ? <span className="block text-xs text-gray-500">{item.actor_role}</span> : null}
                      </td>
                      <td className="max-w-md text-sm text-gray-700">{item.summary}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selected ? (
        <RecordHistoryDrawer
          entityType={selected.entity_type}
          entityId={selected.entity_id}
          title={selected.entity_label || 'Histórico do registro'}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </FormPageShell>
  );
};

export default SystemHistory;
